import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@packages/api';
import { listClients } from '@packages/api/endpoints/client.js';
import { listClientProjects } from '@packages/api/endpoints/client-project.js';
import { listInvoices } from '@packages/api/endpoints/invoice.js';
import { InvoiceStatusSchema } from '@packages/types';
import { useCopy } from '../../hooks/useCopy';
import { forgetRecents, readRecents, type RecentKind, type RecentsOwner } from '../../lib/recents';
import {
  clientRowFromList,
  invoiceRowFromList,
  matterRowFromList,
  type OmniboxGroup,
  type OmniboxRow,
} from './omnibox.items';

/** Rows shown per kind; storage keeps a few more so pruning a gone record still leaves three. */
const SHOWN_PER_KIND = 3;
const ALL_INVOICE_STATUSES = InvoiceStatusSchema.options;

interface KindRecents {
  rows: OmniboxRow[];
  fromHistory: boolean;
  isPending: boolean;
  isError: boolean;
}

function resolveRows<T extends { id: string }>(
  response: T[] | undefined,
  ids: string[],
  toRow: (item: T) => OmniboxRow,
): { rows: OmniboxRow[]; missing: string[] } {
  if (!response) return { rows: [], missing: [] };
  if (ids.length === 0) return { rows: response.map(toRow), missing: [] };
  // A row we did not ask for means the server ignored `ids` (an old backend
  // mid-deploy): render nothing and prune nothing rather than wipe good history.
  const requested = new Set(ids);
  if (response.some((item) => !requested.has(item.id))) return { rows: [], missing: [] };
  const byId = new Map(response.map((item) => [item.id, item]));
  return {
    rows: ids.flatMap((id) => byId.get(id) ?? []).slice(0, SHOWN_PER_KIND).map(toRow),
    missing: ids.filter((id) => !byId.has(id)),
  };
}

/**
 * One kind's recents: the stored ids resolved through the list endpoint's `ids`
 * filter, or one cheap "latest" list call when nothing is stored for the kind -
 * or when every stored id turned out to be gone, so the same open falls back
 * rather than showing nothing until the next one.
 * Never sends an empty `ids` array (the server reads it as "no filter").
 */
function useKindRecents<T extends { id: string }>({
  owner,
  kind,
  enabled,
  ids,
  keyPrefix,
  fetchByIds,
  fetchLatest,
  toRow,
}: {
  owner: RecentsOwner;
  kind: RecentKind;
  enabled: boolean;
  ids: string[];
  keyPrefix: readonly unknown[];
  fetchByIds: (ids: string[]) => Promise<{ data: T[] }>;
  fetchLatest: () => Promise<{ data: T[] }>;
  toRow: (item: T) => OmniboxRow;
}): KindRecents {
  const hasHistory = ids.length > 0;
  const history = useQuery({
    queryKey: [...keyPrefix, 'omniboxRecents', ids],
    queryFn: () => fetchByIds(ids),
    enabled: enabled && hasHistory,
  });

  const resolved = resolveRows(history.data?.data, ids, toRow);
  const historyExhausted = hasHistory && history.isSuccess && resolved.missing.length === ids.length;
  const useLatest = !hasHistory || historyExhausted;

  const latest = useQuery({
    queryKey: [...keyPrefix, 'omniboxFallback'],
    queryFn: fetchLatest,
    enabled: enabled && useLatest,
  });

  // Keyed on the joined ids: `missing` is a fresh array every render.
  const missingKey = resolved.missing.join(',');
  useEffect(() => {
    forgetRecents(owner, kind, missingKey === '' ? [] : missingKey.split(','));
  }, [owner, kind, missingKey]);

  const active = useLatest ? latest : history;
  const rows = useLatest ? resolveRows(latest.data?.data, [], toRow).rows : resolved.rows;
  return {
    rows,
    fromHistory: !useLatest,
    isPending: enabled && active.isPending,
    isError: enabled && active.isError,
  };
}

/**
 * What the "Search or ask Bill" field shows before anything is typed: per kind,
 * the records this person opened most recently on this device, falling back to
 * the account's latest when a kind has no history (`fromHistory` says which, per
 * kind). Reads storage on each open so visits from another tab appear.
 */
export function useOmniboxRecents({
  accountId,
  userId,
  open,
  invoicesVisible,
}: {
  accountId: string;
  userId: string;
  open: boolean;
  invoicesVisible: boolean;
}): { groups: OmniboxGroup[]; isLoading: boolean; isError: boolean; fromHistory: Record<RecentKind, boolean> } {
  const copy = useCopy();
  const owner = useMemo(() => ({ accountId, userId }), [accountId, userId]);
  // Re-read on every open, so a visit recorded since the last open shows up.
  const stored = useMemo(
    () => (open ? readRecents(owner) : { client: [], matter: [], invoice: [] }),
    [open, owner],
  );

  const clients = useKindRecents({
    owner,
    kind: 'client',
    enabled: open,
    ids: stored.client,
    keyPrefix: queryKeys.clients(accountId),
    fetchByIds: (ids) => listClients(accountId, { ids, limit: ids.length }),
    fetchLatest: () =>
      listClients(accountId, { sortBy: 'lastActivityDate', sortDirection: 'desc', limit: SHOWN_PER_KIND }),
    toRow: clientRowFromList,
  });
  const matters = useKindRecents({
    owner,
    kind: 'matter',
    enabled: open,
    ids: stored.matter,
    keyPrefix: queryKeys.clientProjects(accountId),
    fetchByIds: (ids) => listClientProjects(accountId, { ids, limit: ids.length }),
    fetchLatest: () =>
      listClientProjects(accountId, { sortField: 'createdAt', sortDirection: 'desc', limit: SHOWN_PER_KIND }),
    toRow: (project) => matterRowFromList(project, copy),
  });
  const invoices = useKindRecents({
    owner,
    kind: 'invoice',
    enabled: open && invoicesVisible,
    ids: stored.invoice,
    keyPrefix: queryKeys.invoices(accountId),
    fetchByIds: (ids) => listInvoices(accountId, { ids, status: ALL_INVOICE_STATUSES, limit: ids.length }),
    fetchLatest: () =>
      listInvoices(accountId, {
        status: ALL_INVOICE_STATUSES,
        sortBy: 'date',
        sortDirection: 'desc',
        limit: SHOWN_PER_KIND,
      }),
    toRow: (invoice) => invoiceRowFromList(invoice, copy),
  });

  const groups: OmniboxGroup[] = [];
  if (clients.rows.length > 0) {
    groups.push({
      heading: copy(clients.fromHistory ? 'omnibox:groups.recentClients' : 'omnibox:groups.activeClients'),
      rows: clients.rows,
    });
  }
  if (matters.rows.length > 0) {
    groups.push({
      heading: copy(matters.fromHistory ? 'omnibox:groups.recentMatters' : 'omnibox:groups.newestMatters'),
      rows: matters.rows,
    });
  }
  if (invoicesVisible && invoices.rows.length > 0) {
    groups.push({
      heading: copy(invoices.fromHistory ? 'omnibox:groups.recentInvoices' : 'omnibox:groups.latestInvoices'),
      rows: invoices.rows,
    });
  }

  const kinds = invoicesVisible ? [clients, matters, invoices] : [clients, matters];
  return {
    groups,
    isLoading: open && kinds.some((kind) => kind.isPending),
    isError: open && kinds.every((kind) => kind.isError),
    fromHistory: { client: clients.fromHistory, matter: matters.fromHistory, invoice: invoices.fromHistory },
  };
}
